// notionTheme.js - Tema inspirado en el estilo Notion para la aplicación
import { createTheme } from '@mui/material/styles';

// Paleta de colores al estilo Notion
const notionPalette = {
  light: {
    primary: {
      main: '#2E2E2E',
      light: '#565656',
      dark: '#000000',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#667EEA',
      light: '#9EAEF2',
      dark: '#3652E3',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#FFFFFF',
      paper: '#FFFFFF',
      sidebar: '#F5F5F5',
      header: '#FFFFFF',
    },
    divider: '#E0E0E0',
    text: {
      primary: '#37352F',
      secondary: '#6B6B6B',
      disabled: '#9E9E9E',
    },
    action: {
      active: '#2E2E2E',
      hover: 'rgba(0, 0, 0, 0.04)',
      selected: 'rgba(0, 0, 0, 0.08)',
    },
    grey: {
      50: '#FAFAFA',
      100: '#F5F5F5',
      200: '#EEEEEE',
      300: '#E0E0E0',
      400: '#BDBDBD',
    }
  },
  dark: {
    primary: {
      main: '#E0E0E0',
      light: '#F5F5F5',
      dark: '#BDBDBD',
      contrastText: '#191919',
    },
    secondary: {
      main: '#667EEA',
      light: '#9EAEF2',
      dark: '#3652E3',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#191919',
      paper: '#2D2D2D',
      sidebar: '#252525',
      header: '#2D2D2D',
    },
    divider: '#383838',
    text: {
      primary: '#E0E0E0',
      secondary: '#BDBDBD',
      disabled: '#757575',
    },
    action: {
      active: '#E0E0E0',
      hover: 'rgba(255, 255, 255, 0.08)',
      selected: 'rgba(255, 255, 255, 0.12)',
    },
    grey: {
      50: '#323232',
      100: '#2D2D2D',
      200: '#252525',
      300: '#1E1E1E',
      400: '#191919',
    }
  }
};

// Componentes personalizados para el estilo Notion
const getNotionComponents = (mode) => ({
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        scrollbarWidth: 'thin',
        '&::-webkit-scrollbar': {
          width: '0.4em',
          height: '0.4em',
        },
        '&::-webkit-scrollbar-track': {
          background: mode === 'light' ? '#f1f1f1' : '#2D2D2D',
        },
        '&::-webkit-scrollbar-thumb': {
          background: mode === 'light' ? '#c1c1c1' : '#555',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: mode === 'light' ? '#a1a1a1' : '#666',
        },
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        boxShadow: mode === 'light' 
          ? '0 1px 2px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.1)' 
          : '0 1px 2px rgba(0, 0, 0, 0.3), 0 1px 3px rgba(0, 0, 0, 0.15)',
        borderRadius: '4px',
      },
      elevation1: {
        boxShadow: mode === 'light' 
          ? '0 1px 2px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.1)' 
          : '0 1px 2px rgba(0, 0, 0, 0.3), 0 1px 3px rgba(0, 0, 0, 0.15)',
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontWeight: 500,
        borderRadius: '4px',
        padding: '6px 14px',
      },
      contained: {
        boxShadow: 'none',
        '&:hover': {
          boxShadow: 'none',
        },
      },
      outlined: {
        borderWidth: '1px',
      },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: {
        boxShadow: 'none',
        borderBottom: `1px solid ${mode === 'light' ? '#EEEEEE' : '#383838'}`,
      },
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: {
        backgroundColor: mode === 'light' ? '#F5F5F5' : '#252525',
        borderRight: 'none',
      },
    },
  },
  MuiListItem: {
    styleOverrides: {
      root: {
        borderRadius: '4px',
        '&.Mui-selected': {
          backgroundColor: mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.12)',
          '&:hover': {
            backgroundColor: mode === 'light' ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.16)',
          },
        },
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontWeight: 500,
        fontSize: '0.9rem',
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: '4px',
        },
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        overflow: 'visible',
        borderRadius: '6px',
      },
    },
  },
  MuiCardHeader: {
    styleOverrides: {
      title: {
        fontSize: '1.125rem',
        fontWeight: 600,
      },
    },
  },
  MuiTypography: {
    styleOverrides: {
      h1: {
        fontSize: '2.25rem',
        fontWeight: 600,
      },
      h2: {
        fontSize: '1.875rem',
        fontWeight: 600,
      },
      h3: {
        fontSize: '1.5rem',
        fontWeight: 600,
      },
      h4: {
        fontSize: '1.25rem',
        fontWeight: 600,
      },
      h5: {
        fontSize: '1.125rem',
        fontWeight: 600,
      },
      h6: {
        fontSize: '1rem',
        fontWeight: 600,
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: '4px',
        fontWeight: 500,
      },
    },
  },
  MuiDivider: {
    styleOverrides: {
      root: {
        margin: '8px 0',
      },
    },
  },
});

// Crear temas para modo claro y oscuro
export const createNotionTheme = (mode) => {
  const palette = mode === 'light' ? notionPalette.light : notionPalette.dark;
  
  return createTheme({
    palette: {
      mode,
      ...palette,
    },
    typography: {
      fontFamily: [
        '-apple-system',
        'BlinkMacSystemFont',
        'Segoe UI',
        'Roboto',
        'Helvetica Neue',
        'Arial',
        'sans-serif',
      ].join(','),
      fontSize: 14,
      fontWeightLight: 300,
      fontWeightRegular: 400,
      fontWeightMedium: 500,
      fontWeightBold: 600,
    },
    shape: {
      borderRadius: 4,
    },
    components: getNotionComponents(mode),
    mixins: {
      toolbar: {
        minHeight: 56,
      },
    },
    transitions: {
      easing: {
        easeOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
        sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
        easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
      },
      duration: {
        shortest: 150,
        shorter: 200,
        short: 250,
        standard: 300,
        complex: 375,
        enteringScreen: 225,
        leavingScreen: 195,
      },
    },
  });
};

export const lightNotionTheme = createNotionTheme('light');
export const darkNotionTheme = createNotionTheme('dark');
